import { z } from "zod";
import { POLICY_BOUNDS, POLICY_DEFAULTS } from "../policy/bounds.js";
import { BASE_POLICY } from "../policy/compile.js";

/**
 * How a trade is managed once it starts paying, expressed as a decision rather than as a
 * rule table.
 *
 * `rule_configs.rules` is the thing the engine executes and stays exactly as it was. This
 * is the layer above it: a named preset plus a handful of parameters, which the compiler in
 * `policy/compile.ts` turns into that rule table at WRITE time. The raw editor remains as
 * Advanced mode, and a policy of `null` is precisely what "this channel is on Advanced
 * mode" means.
 */

export const managementPresetSchema = z.enum([
  /** Nothing happens after entry. The stop stays where the signal put it. */
  "hold",
  /** Stop to breakeven at TP1, exactly on the entry. */
  "be_at_tp1",
  /** Breakeven at TP1 with an adverse spread buffer, so an ordinary retest cannot tag it. */
  "be_buffer",
  /** Stop to a fraction of the original risk at TP1, rather than all the way to breakeven. */
  "half_risk",
  /** Breakeven-plus-buffer at TP1, then trail the stop to TP1's level when TP2 pays. */
  "ladder",
  /**
   * Not compilable, and deliberately so: it is the name the UI gives to Advanced mode, so
   * that "which mode is this channel in" is answerable from the policy alone.
   */
  "custom",
]);
export type ManagementPreset = z.infer<typeof managementPresetSchema>;

/**
 * How much of the channel's own management to obey. Stored beside the policy rather than
 * inside it, because Advanced mode sets the policy to null and a per-channel behaviour
 * switch must not disappear because someone chose the raw editor.
 */
export const followChannelManagementSchema = z.enum(["all", "closes_only", "ignore"]);
export type FollowChannelManagement = z.infer<typeof followChannelManagementSchema>;

/** The two triggers a preset can hang an extra action on. The rest are Advanced-only. */
export const managementTriggerSchema = z.enum(["TP1_HIT", "TP2_HIT"]);
export type ManagementTrigger = z.infer<typeof managementTriggerSchema>;

export const managementPolicySchema = z.object({
  version: z.literal(1).default(1),
  preset: managementPresetSchema.default(POLICY_DEFAULTS.preset),

  /*
   * Every preset's parameters live side by side and are PRESERVED across preset changes:
   * the compiler reads only the ones its own preset uses, so half_risk → be_buffer →
   * half_risk round-trips in the card without losing the R value someone typed. Which
   * parameters a preset actually reads is `PRESET_PARAMS` in `policy/compile.ts`, and it is
   * the same table that decides which inputs the card renders.
   */
  beOffsetPips: z
    .number()
    .min(POLICY_BOUNDS.beOffsetPips.min)
    .max(POLICY_BOUNDS.beOffsetPips.max)
    .default(POLICY_DEFAULTS.beOffsetPips),
  bufferSpreads: z
    .number()
    .min(POLICY_BOUNDS.bufferSpreads.min)
    .max(POLICY_BOUNDS.bufferSpreads.max)
    .default(POLICY_DEFAULTS.bufferSpreads),
  stopAtR: z
    .number()
    .min(POLICY_BOUNDS.stopAtR.min)
    .max(POLICY_BOUNDS.stopAtR.max)
    .default(POLICY_DEFAULTS.stopAtR),

  /** Forced on by `ladder` — under that preset the checkbox renders checked and disabled. */
  trailToTp1AfterTp2: z.boolean().default(POLICY_DEFAULTS.trailToTp1AfterTp2),

  partialClose: z
    .object({
      enabled: z.boolean().default(POLICY_DEFAULTS.partialEnabled),
      at: managementTriggerSchema.default(POLICY_DEFAULTS.partialAt),
      pct: z
        .number()
        .min(POLICY_BOUNDS.partialPct.min)
        .max(POLICY_BOUNDS.partialPct.max)
        .default(POLICY_DEFAULTS.partialPct),
    })
    .default({
      enabled: POLICY_DEFAULTS.partialEnabled,
      at: POLICY_DEFAULTS.partialAt,
      pct: POLICY_DEFAULTS.partialPct,
    }),

  trailing: z
    .object({
      enabled: z.boolean().default(POLICY_DEFAULTS.trailingEnabled),
      at: managementTriggerSchema.default(POLICY_DEFAULTS.trailingAt),
      /** In OUR pips, not MetaApi's — this broker calls a gold pip 0.01. */
      distancePips: z
        .number()
        .gt(POLICY_BOUNDS.trailingDistancePips.min)
        .max(POLICY_BOUNDS.trailingDistancePips.max)
        .default(POLICY_DEFAULTS.trailingDistancePips),
    })
    .default({
      enabled: POLICY_DEFAULTS.trailingEnabled,
      at: POLICY_DEFAULTS.trailingAt,
      distancePips: POLICY_DEFAULTS.trailingDistancePips,
    }),

  cancelPendingOnSignalClose: z.boolean().default(POLICY_DEFAULTS.cancelPendingOnSignalClose),
  closeRemainingOnSignalClose: z.boolean().default(POLICY_DEFAULTS.closeRemainingOnSignalClose),
});
export type ManagementPolicy = z.infer<typeof managementPolicySchema>;

/**
 * What `orders.trailing` holds once a server-side trailing stop is live on an order.
 *
 * Written only AFTER the broker accepts the call — a flag written ahead of it would stand
 * our own rules down while nothing was actually trailing, which is worse than not having the
 * feature. `distancePrice` is recorded beside the pips because it is what was actually sent:
 * MetaApi's own pip is not ours, and a month later "20 pips" alone would not say which.
 */
export const orderTrailingSchema = z.object({
  distancePips: z.number(),
  distancePrice: z.number(),
  setAt: z.string(),
});
export type OrderTrailing = z.infer<typeof orderTrailingSchema>;

/**
 * The cold-start policy, and the one every channel is effectively on today: it compiles to
 * exactly `DEFAULT_RULE_SET`, which is asserted in `policy/compile.test.ts`. That equality
 * is what lets an existing channel's seeded rules open in the preset card instead of
 * dumping Andy into the raw editor.
 */
export const DEFAULT_MANAGEMENT_POLICY: ManagementPolicy = managementPolicySchema.parse(BASE_POLICY);
